import axios from 'axios'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Label } from 'shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { Button } from 'shared/ui/button'
import { Alert, AlertDescription, AlertTitle } from 'shared/ui/alert'
import { Badge } from 'shared/ui/badge'
import { Loader2, MapPin, MousePointer2, RefreshCcw, Satellite } from 'lucide-react'
import {
  getDefaultGps,
  getPlotPlans,
  type PlotPlanGps,
  type PlotPlanOption,
} from 'modules/permit/api/workAreaService'
import { GpsPickerMap } from './GpsPickerMap'

export type WorkAreaMapMode = 'plot-plan' | 'gps'

export interface WorkAreaMarker {
  xPercent: number
  yPercent: number
}

export interface WorkAreaRectangle {
  xPercent: number
  yPercent: number
  widthPercent: number
  heightPercent: number
}

export interface GpsAreaBounds {
  north: number
  south: number
  east: number
  west: number
}

export interface WorkAreaSelection {
  mapMode: WorkAreaMapMode
  plotPlanId?: string
  plotPlanVersionId?: string
  plotPlanName?: string
  plotPlanImagePath?: string | null
  marker?: WorkAreaMarker | null
  area?: WorkAreaRectangle | null
  areas?: WorkAreaRectangle[] | null
  gpsLatitude?: number | null
  gpsLongtitude?: number | null
  gpsAreas?: GpsAreaBounds[] | null
  gpsSourceLocationId?: string | null
}

interface WorkAreaSectionProps {
  tenantId?: string
  locationId?: string
  unitId?: string
  locationLabel?: string
  selection?: WorkAreaSelection
  setSelection?: Dispatch<SetStateAction<WorkAreaSelection>>
  isReadOnly?: boolean
}

const MAP_MODE_OPTIONS: { value: WorkAreaMapMode; label: string }[] = [
  { value: 'plot-plan', label: 'Plot Plan (ภาพโรงงาน)' },
  { value: 'gps', label: 'GPS' },
]

const MIN_AREA_SIZE = 1.5
const hasNumericCoordinate = (value?: number | null): value is number =>
  typeof value === 'number' && Number.isFinite(value)
const isGuidLike = (value?: string | null) =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim())
const stripLocationCode = (value?: string | null) => {
  if (!value) return null
  const trimmed = value.trim()
  if (!trimmed) return null
  return trimmed.replace(/^\d+\s*:\s*/, '').trim()
}

const resolveAssetUrl = (path?: string | null) => {
  if (!path) return ''
  if (/^https?:\/\//i.test(path)) return path
  const normalised = path.replace(/\\/g, '/').replace(/^\/+/, '')
  const lower = normalised.toLowerCase()
  const withPrefix = lower.startsWith('images/')
    ? normalised
    : lower.startsWith('attachments/')
      ? normalised
      : lower.startsWith('plotplans/')
        ? `attachments/${normalised}`
        : `images/${normalised}`
  const baseUrl = process.env.REACT_APP_API_BASE_URL
  if (baseUrl) {
    return `${baseUrl.replace(/\/$/, '')}/${withPrefix}`
  }
  return `/${withPrefix}`
}

const DEFAULT_SELECTION: WorkAreaSelection = {
  mapMode: 'plot-plan',
  marker: null,
  area: null,
  areas: null,
  gpsLatitude: null,
  gpsLongtitude: null,
  gpsAreas: null,
}

export function WorkAreaSection({
  locationId,
  unitId,
  locationLabel,
  selection,
  setSelection,
  isReadOnly = false,
}: WorkAreaSectionProps = {}) {
  const [plotPlans, setPlotPlans] = useState<PlotPlanOption[]>([])
  const [isLoadingPlotPlans, setIsLoadingPlotPlans] = useState(false)
  const [plotPlanError, setPlotPlanError] = useState<string | null>(null)
  const [resolvedPlotPlanImageSrc, setResolvedPlotPlanImageSrc] = useState<string>('')
  const [isPlotPlanImageLoading, setIsPlotPlanImageLoading] = useState(false)
  const [plotPlanImageError, setPlotPlanImageError] = useState<string | null>(null)

  const [defaultGps, setDefaultGps] = useState<PlotPlanGps | null>(null)
  const [gpsError, setGpsError] = useState<string | null>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [draftArea, setDraftArea] = useState<WorkAreaRectangle | null>(null)
  const draftAreaRef = useRef<WorkAreaRectangle | null>(null)

  const imageContainerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const [internalSelection, setInternalSelection] = useState<WorkAreaSelection>(
    selection ?? DEFAULT_SELECTION,
  )
  const selectionRef = useRef<WorkAreaSelection>(selection ?? DEFAULT_SELECTION)
  const startPointRef = useRef<{ x: number; y: number } | null>(null)
  const internalUpdate = useCallback(
    (updater: SetStateAction<WorkAreaSelection>) => {
      setInternalSelection((prev) =>
        typeof updater === 'function'
          ? (updater as (value: WorkAreaSelection) => WorkAreaSelection)(prev)
          : updater,
      )
    },
    [],
  )
  const updateSelection = useCallback(
    (updater: SetStateAction<WorkAreaSelection>) => {
      if (setSelection) {
        setSelection(updater)
      } else {
        internalUpdate(updater)
      }
    },
    [internalUpdate, setSelection],
  )
  const currentSelection = selection ?? internalSelection

  useEffect(() => {
    if (selection) {
      setInternalSelection(selection)
    }
  }, [selection])

  useEffect(() => {
    selectionRef.current = currentSelection ?? DEFAULT_SELECTION
  }, [currentSelection])

  const calculatePercentPoint = useCallback((event: React.MouseEvent) => {
    if (!imageContainerRef.current) return null
    const rect = imageContainerRef.current.getBoundingClientRect()
    const x = ((event.clientX - rect.left) / rect.width) * 100
    const y = ((event.clientY - rect.top) / rect.height) * 100
    return {
      x: Math.max(0, Math.min(100, Number.parseFloat(x.toFixed(2)))),
      y: Math.max(0, Math.min(100, Number.parseFloat(y.toFixed(2)))),
    }
  }, [])

  const computeAreaFromPoints = useCallback(
    (start: { x: number; y: number }, current: { x: number; y: number }): WorkAreaRectangle => {
      const left = Math.min(start.x, current.x)
      const top = Math.min(start.y, current.y)
      const width = Math.abs(current.x - start.x)
      const height = Math.abs(current.y - start.y)
      return {
        xPercent: Number.parseFloat(left.toFixed(2)),
        yPercent: Number.parseFloat(top.toFixed(2)),
        widthPercent: Number.parseFloat(width.toFixed(2)),
        heightPercent: Number.parseFloat(height.toFixed(2)),
      }
    },
    [],
  )

  const renderAreaOverlay = useCallback((area: WorkAreaRectangle, variant: 'final' | 'draft' = 'final') => {
    if (!area) return null
    const baseClasses =
      variant === 'final'
        ? 'border-blue-600 bg-blue-500/30'
        : 'border-blue-400 border-dashed bg-blue-300/20'
    return (
      <div
        className={`absolute pointer-events-none border-2 ${baseClasses}`}
        style={{
          left: `${area.xPercent}%`,
          top: `${area.yPercent}%`,
          width: `${Math.max(area.widthPercent, 0.5)}%`,
          height: `${Math.max(area.heightPercent, 0.5)}%`,
        }}
      />
    )
  }, [])

  const hasValidLocation = Boolean(locationId)

  const baseSelection = currentSelection ?? DEFAULT_SELECTION

  const activeMapMode: WorkAreaMapMode = baseSelection.mapMode ?? 'plot-plan'
  const canInteract = hasValidLocation && !isReadOnly

  const normalizedSelectedPlotPlanName = baseSelection.plotPlanName?.trim().toLowerCase() ?? ''
  const hasSelectedPlotPlan = Boolean(
    baseSelection.plotPlanId ||
      baseSelection.plotPlanVersionId ||
      normalizedSelectedPlotPlanName ||
      baseSelection.plotPlanImagePath,
  )
  const selectedPlotPlan = useMemo(
    () => {
      if (!hasSelectedPlotPlan || plotPlans.length === 0) {
        return null
      }

      return (
        plotPlans.find((plan) => plan.plotPlanId === baseSelection.plotPlanId) ??
        plotPlans.find((plan) => plan.plotPlanVersionId === baseSelection.plotPlanVersionId) ??
        plotPlans.find((plan) => plan.name.trim().toLowerCase() === normalizedSelectedPlotPlanName) ??
        null
      )
    },
    [
      baseSelection.plotPlanId,
      baseSelection.plotPlanVersionId,
      hasSelectedPlotPlan,
      normalizedSelectedPlotPlanName,
      plotPlans,
    ],
  )
  const effectivePlotPlanImagePath =
    selectedPlotPlan?.backgroundPath ?? (hasSelectedPlotPlan ? null : (baseSelection.plotPlanImagePath ?? null))
  const plotPlanImageSrc = useMemo(
    () => resolveAssetUrl(effectivePlotPlanImagePath),
    [effectivePlotPlanImagePath],
  )
  const shouldWaitForPlotPlanResolution =
    activeMapMode === 'plot-plan' && hasValidLocation && hasSelectedPlotPlan && isLoadingPlotPlans

  const activeAreas =
    baseSelection.areas && baseSelection.areas.length > 0
      ? baseSelection.areas
      : baseSelection.area
        ? [baseSelection.area]
        : []
  const activeArea = activeAreas.length > 0 ? activeAreas[activeAreas.length - 1] : null
  const displayMarker = activeAreas.length === 0 && baseSelection.marker ? baseSelection.marker : null

  useEffect(() => {
    draftAreaRef.current = draftArea
  }, [draftArea])

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null

    if (shouldWaitForPlotPlanResolution) {
      setResolvedPlotPlanImageSrc('')
      setIsPlotPlanImageLoading(true)
      setPlotPlanImageError(null)
      return
    }

    if (!plotPlanImageSrc) {
      setResolvedPlotPlanImageSrc('')
      setIsPlotPlanImageLoading(false)
      setPlotPlanImageError(hasSelectedPlotPlan ? 'ไม่สามารถโหลดรูป Work Area ได้' : null)
      return
    }

    setResolvedPlotPlanImageSrc('')
    setIsPlotPlanImageLoading(true)
    setPlotPlanImageError(null)

    axios
      .get<Blob>(plotPlanImageSrc, { responseType: 'blob' })
      .then((response) => {
        if (cancelled) {
          return
        }

        const blob = response.data
        if (!blob || blob.size === 0) {
          setPlotPlanImageError('ไม่สามารถโหลดรูป Work Area ได้')
          return
        }

        objectUrl = URL.createObjectURL(blob)
        setResolvedPlotPlanImageSrc(objectUrl)
      })
      .catch(() => {
        if (!cancelled) {
          setPlotPlanImageError('ไม่สามารถโหลดรูป Work Area ได้')
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsPlotPlanImageLoading(false)
        }
      })

    return () => {
      cancelled = true
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [hasSelectedPlotPlan, plotPlanImageSrc, shouldWaitForPlotPlanResolution])

  useEffect(() => {
    if (!isDrawing) {
      setDraftArea(null)
    }
  }, [baseSelection.area, baseSelection.areas, isDrawing])

  const handleMapModeChange = useCallback(
    (mode: WorkAreaMapMode) => {
      updateSelection((prev) => ({
        ...prev,
        mapMode: mode,
        marker: null,
        area: mode === 'plot-plan' ? prev.area ?? null : null,
        areas:
          mode === 'plot-plan'
            ? prev.areas && prev.areas.length > 0
              ? prev.areas
              : prev.area
                ? [prev.area]
                : null
            : null,
        ...(mode === 'plot-plan'
          ? {
              gpsLatitude: null,
              gpsLongtitude: null,
              gpsSourceLocationId: null,
            }
          : {
              plotPlanId: undefined,
              plotPlanVersionId: undefined,
              plotPlanName: undefined,
              plotPlanImagePath: undefined,
            }),
      }))
    },
    [updateSelection],
  )

  const handlePlotPlanSelect = useCallback(
    (value: string) => {
      const plan = plotPlans.find((item) => item.plotPlanId === value)
      updateSelection((prev) => ({
        ...prev,
        plotPlanId: plan?.plotPlanId,
        plotPlanVersionId: plan?.plotPlanVersionId,
        plotPlanName: plan?.name,
        plotPlanImagePath: plan?.backgroundPath ?? null,
        marker: null,
        area: null,
        areas: null,
      }))
    },
    [plotPlans, updateSelection],
  )

  const handleAreaReset = useCallback(() => {
    setDraftArea(null)
    updateSelection((prev) => ({
      ...prev,
      marker: null,
      area: null,
      areas: null,
    }))
  }, [updateSelection])

  const finalizeArea = useCallback(
    (area: WorkAreaRectangle | null) => {
      if (!area) {
        updateSelection((prev) => ({
          ...prev,
          area: null,
          areas: null,
          marker: null,
        }))
        return
      }
      updateSelection((prev) => ({
        ...prev,
        area,
        areas: [...(prev.areas ?? (prev.area ? [prev.area] : [])), area],
        marker: null,
      }))
    },
    [updateSelection],
  )

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (!canInteract) {
        return
      }
      const point = calculatePercentPoint(event)
      if (!point) {
        return
      }
      startPointRef.current = point
      setIsDrawing(true)
      setDraftArea({
        xPercent: point.x,
        yPercent: point.y,
        widthPercent: 0,
        heightPercent: 0,
      })
    },
    [calculatePercentPoint, canInteract],
  )

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!isDrawing || !startPointRef.current || !canInteract) {
        return
      }
      const point = calculatePercentPoint(event)
      if (!point) {
        return
      }
      const area = computeAreaFromPoints(startPointRef.current, point)
      setDraftArea(area)
    },
    [calculatePercentPoint, canInteract, computeAreaFromPoints, isDrawing],
  )

  const finishDrawing = useCallback(
    (commit: boolean) => {
      if (!isDrawing) {
        return
      }
      setIsDrawing(false)
      const area = draftAreaRef.current
      if (commit && area && area.widthPercent >= MIN_AREA_SIZE && area.heightPercent >= MIN_AREA_SIZE) {
        finalizeArea(area)
      }
      setDraftArea(null)
      startPointRef.current = null
    },
    [finalizeArea, isDrawing],
  )

  const handleMouseUp = useCallback(() => {
    finishDrawing(true)
  }, [finishDrawing])

  const handleMouseLeave = useCallback(() => {
    finishDrawing(false)
  }, [finishDrawing])

  const loadDefaultGps = useCallback((applyToSelection: boolean) => {
    if (!locationId) {
      return
    }

    setGpsError(null)

    getDefaultGps(locationId)
      .then((gps) => {
        setDefaultGps(gps)
        if (applyToSelection) {
          updateSelection((prev) => ({
            ...prev,
            gpsLatitude: gps?.latitude ?? null,
            gpsLongtitude: gps?.longtitude ?? null,
            gpsSourceLocationId: locationId,
            plotPlanId: gps?.plotPlanId ?? prev.plotPlanId,
            plotPlanName: gps?.name ?? prev.plotPlanName,
            area: null,
            areas: null,
            gpsAreas: null,
            marker: null,
          }))
        }
      })
      .catch(() => {
        setDefaultGps(null)
        setGpsError('ไม่สามารถดึงข้อมูลตำแหน่ง GPS ได้')
      })
  }, [locationId, updateSelection])

  const handleGpsMapChange = useCallback(
    (payload: {
      latitude: number
      longitude: number
      gpsAreas: GpsAreaBounds[]
    }) => {
      updateSelection((prev) => ({
        ...prev,
        gpsLatitude: payload.latitude,
        gpsLongtitude: payload.longitude,
        gpsSourceLocationId: locationId ?? prev.gpsSourceLocationId ?? null,
        area: null,
        areas: null,
        gpsAreas: payload.gpsAreas.length > 0 ? payload.gpsAreas : null,
        marker: null,
      }))
    },
    [locationId, updateSelection],
  )

  useEffect(() => {
    if (!hasValidLocation || activeMapMode !== 'plot-plan') {
      setPlotPlans([])
      setPlotPlanError(null)
      return
    }

    let cancelled = false
    setIsLoadingPlotPlans(true)
    setPlotPlanError(null)

    getPlotPlans(locationId!)
      .then((plans) => {
        if (cancelled) {
          return
        }

        setPlotPlans(plans)

        const currentSelection = selectionRef.current
        const currentPlotPlanId = currentSelection?.plotPlanId

        if (currentPlotPlanId) {
          const matchedPlan = plans.find((plan) => plan.plotPlanId === currentPlotPlanId)
          if (!matchedPlan) {
            updateSelection((prev) => {
              if (!prev.plotPlanId) {
                return prev
              }
              return {
                ...prev,
                plotPlanId: undefined,
                plotPlanVersionId: undefined,
                plotPlanName: undefined,
                plotPlanImagePath: undefined,
                marker: null,
              }
            })
          } else {
            updateSelection((prev) => {
              const hasChanges =
                prev.plotPlanVersionId !== matchedPlan.plotPlanVersionId ||
                (matchedPlan.backgroundPath ?? null) !== prev.plotPlanImagePath ||
                matchedPlan.name !== prev.plotPlanName

              if (!hasChanges) {
                return prev
              }

              return {
                ...prev,
                plotPlanVersionId: matchedPlan.plotPlanVersionId,
                plotPlanImagePath: matchedPlan.backgroundPath ?? null,
                plotPlanName: matchedPlan.name,
              }
            })
          }
        } else if (plans.length === 1) {
          const solePlan = plans[0]
          updateSelection((prev) => {
            if (prev.plotPlanId === solePlan.plotPlanId) {
              return prev
            }
            return {
              ...prev,
              plotPlanId: solePlan.plotPlanId,
              plotPlanVersionId: solePlan.plotPlanVersionId,
              plotPlanName: solePlan.name,
              plotPlanImagePath: solePlan.backgroundPath ?? null,
            }
          })
        }
      })
      .catch(() => {
        setPlotPlanError('ไม่สามารถโหลดข้อมูล Plot Plan ได้')
        setPlotPlans([])
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingPlotPlans(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [activeMapMode, hasValidLocation, locationId, updateSelection])

  useEffect(() => {
    if (!hasValidLocation || activeMapMode !== 'gps') {
      setDefaultGps(null)
      return
    }

    const currentGps = selectionRef.current
    const locationChanged =
      !!currentGps?.gpsSourceLocationId && currentGps.gpsSourceLocationId !== locationId
    const hasExistingGps =
      hasNumericCoordinate(currentGps?.gpsLatitude) &&
      hasNumericCoordinate(currentGps?.gpsLongtitude) &&
      !locationChanged

    loadDefaultGps(locationChanged || !hasExistingGps)
  }, [activeMapMode, hasValidLocation, loadDefaultGps, locationId])

  const showPlotPlanSection = activeMapMode === 'plot-plan'
  const showGpsSection = activeMapMode === 'gps'
  const normalizedLocationLabel = stripLocationCode(locationLabel)
  const normalizedDefaultGpsLabel = stripLocationCode(defaultGps?.name)
  const resolvedLocationLabel =
    (normalizedLocationLabel && !isGuidLike(normalizedLocationLabel) ? normalizedLocationLabel : null) ||
    (normalizedDefaultGpsLabel && !isGuidLike(normalizedDefaultGpsLabel) ? normalizedDefaultGpsLabel : null) ||
    baseSelection.plotPlanName?.trim() ||
    null

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <MapPin className="h-5 w-5 text-orange-500" />
          กำหนดพื้นที่ทำงาน (Work Area)
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">โหมดแผนที่</Label>
            <Select
              value={activeMapMode}
              onValueChange={(value) => handleMapModeChange(value as WorkAreaMapMode)}
              disabled={!hasValidLocation || isReadOnly}
            >
              <SelectTrigger className="bg-gray-50 border-gray-200">
                <SelectValue placeholder="เลือกโหมดกำหนดพื้นที่" />
              </SelectTrigger>
              <SelectContent>
                {MAP_MODE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!hasValidLocation ? (
              <p className="text-xs text-muted-foreground">
                กรุณาเลือก Area และ Unit ก่อน
              </p>
            ) : null}
          </div>

          {showPlotPlanSection ? (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">แผนผัง (Plot Plan)</Label>
              <Select
                value={baseSelection.plotPlanId ?? ''}
                onValueChange={handlePlotPlanSelect}
                disabled={!canInteract || isLoadingPlotPlans || plotPlans.length === 0}
              >
                <SelectTrigger className="bg-gray-50 border-gray-200">
                  <SelectValue placeholder="เลือก Plot Plan" />
                </SelectTrigger>
                <SelectContent>
                  {plotPlans.map((plan) => (
                    <SelectItem key={plan.plotPlanId} value={plan.plotPlanId}>
                      {plan.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isLoadingPlotPlans ? (
                <p className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  กำลังโหลด Plot Plan...
                </p>
              ) : null}
              {plotPlanError ? (
                <p className="text-xs text-red-600">{plotPlanError}</p>
              ) : null}
            </div>
          ) : null}

          {showGpsSection ? (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">พิกัด GPS เริ่มต้น</Label>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Satellite className="h-4 w-4 text-sky-500" />
                {resolvedLocationLabel ? (
                  <span>{resolvedLocationLabel}</span>
                ) : (
                  <span>ยังไม่มีค่าพิกัด GPS สำหรับพื้นที่นี้</span>
                )}
              </div>
              <div className="grid grid-cols-1 gap-2 rounded-md border border-dashed border-gray-200 p-3 text-xs text-muted-foreground md:grid-cols-2">
                <div>
                  <span className="font-medium text-gray-700">Latitude:</span>{' '}
                  {baseSelection.gpsLatitude ?? '—'}
                </div>
                <div>
                  <span className="font-medium text-gray-700">Longitude:</span>{' '}
                  {baseSelection.gpsLongtitude ?? '—'}
                </div>
              </div>
              {gpsError ? <p className="text-xs text-red-600">{gpsError}</p> : null}
            </div>
          ) : null}
        </div>

        {showPlotPlanSection ? (
          <div className="space-y-4">
            <Alert variant="default" className="border-blue-200 bg-blue-50 text-blue-800">
              <AlertTitle className="flex items-center gap-2">
                <MousePointer2 className="h-4 w-4" />
                วิธีเลือกพื้นที่ทำงาน
              </AlertTitle>
              <AlertDescription className="text-sm leading-6">
                - ลากเมาส์บนแผนผังเพื่อกำหนดกรอบพื้นที่ทำงาน
                <br />
                - หากต้องการล้างการเลือก ให้กดปุ่ม{' '}
                <Badge variant="secondary" className="align-middle">
                  ล้างพื้นที่
                </Badge>
              </AlertDescription>
            </Alert>

            <div className="overflow-hidden rounded-md border border-gray-200">
              <div className="border-b border-gray-100 bg-gray-50 px-4 py-2">
                <p className="text-sm font-medium text-gray-700">
                  {baseSelection.plotPlanName ?? 'ยังไม่ได้เลือก Plot Plan'}
                </p>
              </div>

              <div
                ref={imageContainerRef}
                className={`relative w-full bg-gray-100 ${canInteract ? 'cursor-crosshair' : 'cursor-not-allowed'}`}
                onMouseDown={canInteract ? handleMouseDown : undefined}
                onMouseMove={canInteract ? handleMouseMove : undefined}
                onMouseUp={canInteract ? handleMouseUp : undefined}
                onMouseLeave={canInteract ? handleMouseLeave : undefined}
              >
                {resolvedPlotPlanImageSrc ? (
                  <>
                    <img
                      ref={imageRef}
                      src={resolvedPlotPlanImageSrc}
                      alt={baseSelection.plotPlanName ?? 'Plot plan'}
                      draggable={false}
                      onDragStart={(event) => event.preventDefault()}
                      onError={() => {
                        setResolvedPlotPlanImageSrc('')
                        setPlotPlanImageError('ไม่สามารถโหลดรูป Work Area ได้')
                      }}
                      className="h-auto w-full select-none pointer-events-none object-contain"
                    />
                    {activeAreas.map((area, index) => (
                      <Fragment key={`area-${index}`}>{renderAreaOverlay(area, 'final')}</Fragment>
                    ))}
                    {draftArea ? renderAreaOverlay(draftArea, 'draft') : null}
                    {displayMarker ? (
                      <div
                        className="absolute -translate-x-1/2 -translate-y-full transform"
                        style={{
                          left: `${displayMarker.xPercent}%`,
                          top: `${displayMarker.yPercent}%`,
                        }}
                      >
                        <MapPin className="h-6 w-6 text-red-600 drop-shadow-sm" />
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div
                    className={`flex min-h-[320px] flex-col items-center justify-center gap-2 px-4 py-8 text-center text-sm text-muted-foreground ${
                      isPlotPlanImageLoading || plotPlanImageError ? '[&_p:last-child]:hidden' : ''
                    }`}
                  >
                    <MapPin className="h-10 w-10 text-gray-400" />
                    {isPlotPlanImageLoading ? (
                      <p>กำลังโหลดรูป Work Area...</p>
                    ) : plotPlanImageError ? (
                      <p className="text-red-600">{plotPlanImageError}</p>
                    ) : null}
                    <p>กรุณาเลือก Plot Plan เพื่อแสดงแผนผัง</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
              <div className="space-y-1">
                <div>
                  <span className="font-medium text-gray-700">ตำแหน่งที่เลือก:</span>{' '}
                  {activeArea
                    ? `${activeAreas.length > 1 ? `เลือก ${activeAreas.length} กล่อง | ` : ''}จุดซ้ายบน (X:${activeArea.xPercent.toFixed(2)}%, Y:${activeArea.yPercent.toFixed(
                        2,
                      )}%) | ขนาด (กว้าง ${activeArea.widthPercent.toFixed(2)}% x สูง ${activeArea.heightPercent.toFixed(
                        2,
                      )}%)`
                    : displayMarker
                      ? `พิกัด (X:${displayMarker.xPercent.toFixed(2)}%, Y:${displayMarker.yPercent.toFixed(2)}%)`
                      : 'ยังไม่ได้กำหนดตำแหน่ง'}
                </div>
                <div>
                  <span className="font-medium text-gray-700">Plot Plan Version:</span>{' '}
                  {baseSelection.plotPlanVersionId ?? '—'}
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAreaReset}
                disabled={!canInteract || (activeAreas.length === 0 && !displayMarker)}
                className="flex items-center gap-1"
              >
                <RefreshCcw className="h-4 w-4" />
                ล้างพื้นที่
              </Button>
            </div>
          </div>
        ) : null}

        {showGpsSection ? (
          <div className="space-y-4">
            <GpsPickerMap
              latitude={baseSelection.gpsLatitude}
              longitude={baseSelection.gpsLongtitude}
              fallbackLatitude={defaultGps?.latitude ?? null}
              fallbackLongitude={defaultGps?.longtitude ?? null}
              canInteract={canInteract}
              gpsAreas={baseSelection.gpsAreas ?? null}
              markerLabel={resolvedLocationLabel}
              instructionText="กดปุ่ม Select เพื่อเริ่ม Mark จุดปฏิบัติงานบนแผนที่"
              onChange={handleGpsMapChange}
            />
          </div>
        ) : null}

        {!hasValidLocation ? (
          <Alert variant="destructive" className="border-orange-200 bg-orange-50 text-orange-900">
            <AlertTitle>ยังไม่สามารถกำหนดพื้นที่ได้</AlertTitle>
            <AlertDescription className="text-sm">
              กรุณาเลือก Area และ Unit ก่อนจึงจะสามารถใช้งาน Plot Plan หรือ GPS ได้
            </AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  )
}
